import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Brain,
  Zap,
  Clock,
  Star,
  Target,
  Loader2,
  BookOpen,
  AlertTriangle,
  Users,
  MapPin
} from 'lucide-react'

/**
 * IntelligentSearch Component
 * AI-powered search using Cloudflare Vectorize for survival content
 */
const IntelligentSearch = ({ userProfile }) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingRecs, setIsLoadingRecs] = useState(false)
  const [searchMode, setSearchMode] = useState('search') // 'search' | 'recommendations' | 'emergency'

  // Load personalized recommendations on component mount
  useEffect(() => {
    if (userProfile && searchMode === 'recommendations') {
      loadRecommendations()
    }
  }, [userProfile, searchMode])

  const loadRecommendations = async () => {
    if (!userProfile) return
    
    setIsLoadingRecs(true)
    try {
      const response = await fetch('/api/intelligence/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userProfile })
      })
      
      if (response.ok) {
        const data = await response.json()
        setRecommendations(data.recommendations || [])
      }
    } catch (error) {
      console.error('Failed to load recommendations:', error)
    }
    setIsLoadingRecs(false)
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!query.trim()) return

    setIsSearching(true)
    try {
      const response = await fetch('/api/intelligence/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          userContext: userProfile || {}
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        setResults(data.results || [])
      }
    } catch (error) {
      console.error('Search failed:', error)
    }
    setIsSearching(false)
  }

  const handleEmergencySearch = async (emergencyQuery) => {
    setQuery(emergencyQuery)
    setIsSearching(true)
    
    try {
      const response = await fetch('/api/intelligence/emergency', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          situation: emergencyQuery,
          location: userProfile?.location || 'general',
          availableResources: []
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        setResults(data.guidance || [])
        setSearchMode('search')
      }
    } catch (error) {
      console.error('Emergency search failed:', error)
    }
    setIsSearching(false)
  }

  const emergencyQueries = [
    "Lost in wilderness without water",
    "Injured and need immediate first aid",
    "Stranded vehicle in storm",
    "Building emergency shelter",
    "Finding food in survival situation"
  ]

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800'
      case 'intermediate': return 'bg-yellow-100 text-yellow-800'
      case 'advanced': return 'bg-orange-100 text-orange-800'
      case 'expert': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'water': return '💧'
      case 'fire': return '🔥'
      case 'shelter': return '🏠'
      case 'medical': return '🏥'
      case 'navigation': return '🧭'
      case 'food': return '🍽️'
      case 'signaling': return '📡'
      default: return '📚'
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Mode Switcher */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSearchMode('search')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
            searchMode === 'search' 
              ? 'bg-green-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <Search className="w-4 h-4" />
          Smart Search
        </button>
        
        {userProfile && (
          <button
            onClick={() => setSearchMode('recommendations')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
              searchMode === 'recommendations' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Brain className="w-4 h-4" />
            Recommendations
          </button>
        )}
        
        <button
          onClick={() => setSearchMode('emergency')}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
            searchMode === 'emergency' 
              ? 'bg-red-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          Emergency
        </button>
      </div>

      {/* Search Interface */}
      {searchMode === 'search' && (
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask anything about survival... (e.g., 'How to purify water in the wilderness?')"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={isSearching}
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !query.trim()}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSearching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Brain className="w-5 h-5" />
              )}
              Search
            </button>
          </form>

          {/* Quick Search Suggestions */}
          <div className="flex flex-wrap gap-2">
            {['water purification', 'build shelter', 'start fire', 'first aid basics', 'navigation'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setQuery(suggestion)}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Emergency Mode */}
      {searchMode === 'emergency' && (
        <div className="mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-red-800">Emergency Situation</h3>
            </div>
            <p className="text-red-700 text-sm">
              Select your emergency situation for immediate, prioritized guidance:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {emergencyQueries.map((emergency, index) => (
              <button
                key={index}
                onClick={() => handleEmergencySearch(emergency)}
                className="p-4 text-left border border-gray-300 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all group"
                disabled={isSearching}
              >
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-red-500 group-hover:text-red-600" />
                  <span className="font-medium text-gray-800 group-hover:text-red-800">
                    {emergency}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Mode */}
      {searchMode === 'recommendations' && userProfile && (
        <div className="mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-800">Personalized for You</h3>
            </div>
            <p className="text-blue-700 text-sm">
              Based on your profile: {userProfile.skillLevel} level, {userProfile.location} environment
            </p>
          </div>
          
          {isLoadingRecs && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-600" />
              <p className="text-gray-600 mt-2">Loading personalized recommendations...</p>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      <AnimatePresence>
        {(results.length > 0 || recommendations.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Search Results */}
            {searchMode === 'search' && results.map((result, index) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getCategoryIcon(result.category)}</span>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {result.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(result.difficulty)}`}>
                        {result.difficulty}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{result.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {result.timeRequired}
                      </div>
                      {result.relevanceScore && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4" />
                          {Math.round(result.relevanceScore * 100)}% match
                        </div>
                      )}
                      {result.urgencyLevel !== undefined && (
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          High Priority
                        </div>
                      )}
                    </div>
                    
                    {result.equipmentNeeded && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Equipment needed:</p>
                        <div className="flex flex-wrap gap-1">
                          {result.equipmentNeeded.map((item, i) => (
                            <span key={i} className="px-2 py-1 bg-gray-100 text-xs rounded">
                              {item.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Learn
                  </button>
                </div>
              </motion.div>
            ))}

            {/* Recommendations */}
            {searchMode === 'recommendations' && recommendations.map((rec, index) => (
              <motion.div
                key={rec.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getCategoryIcon(rec.category)}</span>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {rec.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(rec.difficulty)}`}>
                        {rec.difficulty}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{rec.description}</p>
                    
                    {rec.recommendationReason && (
                      <div className="bg-blue-100 border border-blue-200 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">
                            Why this is recommended: {rec.recommendationReason}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {Math.round(rec.relevanceScore * 100)}% match
                      </div>
                    </div>
                  </div>
                  
                  <button className="ml-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg hover:from-blue-700 hover:to-green-700 transition-all flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Start Learning
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty States */}
      {searchMode === 'search' && query && !isSearching && results.length === 0 && (
        <div className="text-center py-8">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No results found. Try a different search term.</p>
        </div>
      )}

      {searchMode === 'recommendations' && !isLoadingRecs && recommendations.length === 0 && userProfile && (
        <div className="text-center py-8">
          <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No recommendations available. Try updating your profile.</p>
        </div>
      )}
    </div>
  )
}

export default IntelligentSearch